<script setup lang="ts">
    import { FormComponentType } from "@/components/forms/Form/convertFormData";
    import { FORM_HELPERS } from "@/components/forms/Form/helpers";
    import KRepeaterButton from "@/components/forms/groups/Repeater/KRepeaterButton.vue";
    import { useValidation } from "@/components/forms/reusables/useValidation";
    import KLabel from "@/components/forms/subComponents/Label/KLabel.vue";
    import { useStore } from "@/store";
    import { isNonEmptyObject } from "@/tabs/Operation/utilsTs";
    import { computed, defineEmits, getCurrentInstance, inject, nextTick, onMounted, ref, toRefs, watch } from "vue";

    const store = useStore();

    // TODO repeatable är inte en del av Formkit
    interface KRepeaterProps {
        type?: never;
        name: string;
        repeatable?: never;
        // Ska lägg till-knappen visas?
        addButton?: boolean;
        // Ska ta bort-knappen visas?
        removeButton?: boolean;
        // Label för hela repeatern, inte per rad
        label?: string;
        validation?: string | Array<[rule: string, ...args: never]>;
        // Label för lägg till-knappen
        addLabel?: string;
        // Label för ta bort-knappen
        removeLabel?: string;
        /*
            Ska repeatern ha minst 1 rad? Gör så att det alltid finns minst 1 tom rad
            och att första raden inte kan tas bort.
        */
        minimumOneRow?: boolean;
        disabled?: boolean;
        readonly?: boolean;
        initialValueGetter?: (props: {
            currentValue: {
                [key: string]: unknown;
            }[];
        }) => Promise<
            {
                [key: string]: unknown;
            }[]
        >;
        converter?: ({ value }: { value: { id?: string; [key: string]: unknown }[] }) => unknown[]; // Funktion som returnerar förändrade värdet
        /**
         * Ska fältet vara synligt? Override för inställning av synlighet som görs via config
         */
        shouldBeVisible?: boolean;
    }

    // TODO Tillkommer i formkit
    // upControl?: boolean,
    // downControl?: boolean,
    // upControl: false,
    // downControl: false,
    const props = withDefaults(defineProps<KRepeaterProps>(), {
        type: undefined,
        addButton: true,
        removeButton: true,
        minimumOneRow: true,
        disabled: false,
        shouldBeVisible: undefined,
    });
    const emit = defineEmits<{
        (event: "add", payload: { index: number }): void;
    }>();
    const { minimumOneRow, disabled, initialValueGetter, converter, readonly, validation, shouldBeVisible, name } =
        toRefs(props);

    // Behöver hämtas upp på det här sättet, pga formulate. Formulate failar om name hämtas ut från props
    const instance = getCurrentInstance();

    // Används för att avgöra om stylingen ska vara anpassad för 1 fält (single-line) eller fler
    const childrenAmount = ref(instance?.proxy?.$slots?.default?.length ?? 1);
    const repeaterRef = ref();
    const inputData = ref<
        {
            [key: string]: unknown;
        }[]
    >([]);
    const modifiedDisabled = ref(disabled?.value);
    const modifiedReadonly = ref(readonly?.value);

    const configNames = inject<{
        moduleName: string;
        subModuleName: string;
    }>("configNames");

    const { modifiedValidation, expose: useValidationExpose } = useValidation({
        name: instance?.proxy?.$attrs?.name ?? "",
        validation: validation?.value,
        moduleName: configNames?.moduleName,
        subModuleName: configNames?.subModuleName,
    });

    const setDisabled = ({ value }: { value: boolean }) => {
        modifiedDisabled.value = value;
    };

    const setReadonly = ({ value }: { value: boolean }) => {
        modifiedReadonly.value = value;
    };

    // Hitta Formulates interna group-komponent sa att vi kan komma at providers per repeater-rad.
    const findNestedComponentByName = (component: any, componentName: string): any => {
        if (!component) {
            return null;
        }

        if (component?.$options?.name === componentName) {
            return component;
        }

        for (const child of component.$children ?? []) {
            const match = findNestedComponentByName(child, componentName);

            if (match) {
                return match;
            }
        }

        return null;
    };

    // Varje rad i repeatable group har en egen provider som kan visa och kontrollera valideringsfel.
    const getRowProvider = (index: number) => {
        const grouping = findNestedComponentByName(repeaterRef.value, "FormulateGrouping");
        return grouping?.providers?.[index];
    };

    // Anvand befintlig Formulate-validering for en specifik rad utan att satta hela formularet i submit-lage.
    const validateRow = async ({ index }: { index: number }) => {
        const provider = getRowProvider(index);

        if (!provider) {
            return true;
        }

        provider.showErrors?.();

        if (typeof provider.hasValidationErrors === "function") {
            return !(await provider.hasValidationErrors());
        }

        return true;
    };

    defineExpose({
        setDisabled,
        convertData: converter?.value,
        kType: FormComponentType.Repeater,
        setReadonly,
        validateRow,
        rowCount: computed(() => inputData.value.length),
        ...useValidationExpose,
    });

    const internalShouldBeVisible = computed(() =>
        store.getters["config/shouldBeVisibleByName"]({
            ...(configNames ?? {}),
            name: name.value,
            overrideValue: shouldBeVisible?.value,
        })
    );

    const formHelpers = inject<{
        setFieldValue: (props: { name: string; value: unknown }) => void;
        setFieldInitialValue: (props: { name: string; value: unknown }) => void;
    }>(FORM_HELPERS);

    watch(
        inputData,
        () => {
            if (isNonEmptyObject(repeaterRef?.value?.localGroupErrors)) {
                repeaterRef.value.setGroupErrors({});
            }
        },
        { deep: true }
    );

    const handleAddMore = async ({ addMore }: { addMore: () => void }) => {
        const index = inputData.value.length;

        addMore();
        await nextTick();
        emit("add", { index });
    };

    const addMore = () => {
        inputData.value = [...(inputData.value ?? []), {}];
    };

    /**
     * Lägg till en ny tom rad, om det ska vara minimum 1 rad och det inte finns några rader
     */
    const addMinimumRows = ({
        currentValue,
    }: {
        currentValue?: {
            [key: string]: unknown;
        }[];
    }) => {
        if (minimumOneRow.value && (!currentValue || currentValue.length === 0)) {
            addMore();
        }
    };

    /**
     * Sätt startvärde i repeatern
     */
    const setInitialValue = async ({ getter }: { getter: NonNullable<KRepeaterProps["initialValueGetter"]> }) => {
        const initialValue = await getter({ currentValue: inputData.value });

        if (initialValue && instance?.proxy?.$attrs?.name) {
            inputData.value = initialValue;
            // Uppdatera default data i form, som används för check av unsaved data
            formHelpers?.setFieldInitialValue?.({
                // Behöver hämtas upp på det här sättet, pga formulate. Formulate failar om name hämtas ut från props
                name: instance.proxy.$attrs.name,
                value: inputData.value,
            });
        }
    };

    onMounted(async () => {
        if (initialValueGetter?.value) {
            await setInitialValue({ getter: initialValueGetter.value });
        } else {
            addMinimumRows({ currentValue: inputData.value });

            if (instance?.proxy.$attrs.name) {
                formHelpers?.setFieldInitialValue?.({
                    // Behöver hämtas upp på det här sättet, pga formulate. Formulate failar om name hämtas ut från props
                    name: instance.proxy.$attrs.name,
                    value: inputData.value,
                });
            }
        }

        watch(inputData, (newValue) => {
            addMinimumRows({ currentValue: newValue });
        });

        await nextTick();

        /*
            Kolla mängden fält inom repeatern via antal värden satta på index 0.
            Måste göras på det här fulhackiga sättet för att Formulate tycker att repeaters med flera fält
            innehåller 0 children
         */
        childrenAmount.value = Object.keys(repeaterRef?.value?.proxy?.[0] ?? {}).length;
    });
</script>

<template>
    <!--
        TODO Formkit
        outer-class="k-repeater"
        Formkits repeater är en egen typ och inte del av group
    -->
    <FormulateInput
        v-bind="{ ...$attrs, ...$props, ...$slots }"
        ref="repeaterRef"
        repeatable
        :name="name"
        type="group"
        :class="[
            'k-repeater',
            { 'single-line': childrenAmount <= 1, readonly: modifiedReadonly, disabled: modifiedDisabled },
        ]"
        remove-position="after"
        v-model="inputData"
        :readonly="modifiedReadonly"
        :disabled="modifiedDisabled"
        v-if="internalShouldBeVisible"
    >
        <template #label>
            <KLabel v-if="label" :validation="modifiedValidation" :content="label" size="large" class="label" />
        </template>
        <template #default="{ index, model }">
            <slot name="default" :index="index" :model="model" :validation="modifiedValidation" />
        </template>
        <template #addmore="{ addMore }">
            <div v-if="addButton" class="button add-button">
                <KRepeaterButton v-if="addButton" :on-click="() => handleAddMore({ addMore })">
                    <template #icon>
                        <img src="@/assets/plus_circle_blue.svg" />
                    </template>
                    <template #label>
                        {{ addLabel }}
                    </template>
                </KRepeaterButton>
            </div>
        </template>
        <template #remove="{ index, removeItem }">
            <div v-if="removeButton && (!minimumOneRow || index !== 0)" class="button remove-button">
                <KRepeaterButton :index="index" :on-click="removeItem">
                    <template #icon>
                        <img src="@/assets/delete.svg" />
                    </template>
                    <template v-if="removeLabel" #label>
                        {{ removeLabel }}
                    </template>
                </KRepeaterButton>
            </div>
        </template>
    </FormulateInput>
</template>

<style scoped lang="scss">
    @use "@/components/forms/Form/styles/FormContent";
    @import "@/style/form";
    @import "@/style/variables";

    .k-repeater {
        display: grid;
        grid-template-columns: subgrid;
        width: 100%;
        grid-column: 1 / -1;
        margin: 20px 0;

        &.disabled,
        &.readonly {
            ::v-deep [data-is-repeatable] {
                pointer-events: none;
                border-radius: 5px;
            }
        }

        // Döljer knapparna if readonly
        &.readonly {
            .add-button,
            .remove-button {
                display: none;
            }

            ::v-deep [data-is-repeatable] {
                // Exkluderar fältet i Vueselect. Annars hamnar bakgrunden ovanför valt värde
                input:not([type="search"]),
                [data-type="select"] > *,
                .input-number button {
                    background: $color-readonly-background !important;
                }
            }
        }

        // Gör knapparna semi-transparenta if disabled
        &.disabled {
            ::v-deep [data-is-repeatable] {
                opacity: 0.5;
            }
        }

        .remove-button {
            grid-column-end: -1;
            align-self: flex-end;
            justify-self: flex-end;
        }

        .add-button {
            // Räkna med border på content + border på input
            $border-inclusion: 2px;

            display: grid;
            grid-template-columns: subgrid;
            align-items: center;
            height: $form-input-height-default;

            margin-top: calc($form-label-default-line-height + $form-label-default-margin-bottom + $border-inclusion);
        }

        /*
            Anpassa gridden om det finns en add button.
            Add button kommer alltid försöka lägga sig i kolumnen längst till höger
         */
        &:has(.add-button) {
            ::v-deep .formulate-input-wrapper {
                .formulate-input-grouping {
                    grid-column: 1 / -2;
                }
            }
        }

        &.single-line {
            // Om child inte har någon span-* satt på sig
            grid-column: var(--span-two);

            $iteration: 2;

            @while FormContent.$k-form-columns >= $iteration {
                &:has(.span-#{FormContent.get-number-text($iteration)}) {
                    grid-column: var(--span-#{FormContent.get-number-text($iteration + 1)});
                }

                $iteration: $iteration + 1;
            }

            &:has(.span-all) {
                grid-column: 1 / -1;
            }
        }

        .label {
            margin-bottom: 0.5rem;
        }

        // Dölj remove-knappen på första raden
        .formulate-input-group-repeatable:first-child .formulate-input-group-repeatable-remove {
            opacity: unset;
        }

        // Fix för att allt ska aligna ordenligt i nya gridsystemet
        ::v-deep > .formulate-input-wrapper:first-child > label:first-child {
            margin-bottom: $form-row-gap;
        }

        // Propagerar ner gridsystemet i formulate
        ::v-deep .formulate-input-wrapper {
            display: grid;
            grid-template-columns: subgrid;
            grid-column: 1 / -1;

            .formulate-input-group,
            .formulate-input-grouping,
            .formulate-input-group-repeatable {
                display: grid;
                grid-template-columns: subgrid;
                grid-column: 1 / -1;
            }

            .formulate-input-group {
                .formulate-input-grouping {
                    width: 100%;
                    grid-gap: calc($form-row-gap / 2);

                    .formulate-input-group-repeatable {
                        top: unset;
                        row-gap: calc($form-row-gap / 2);
                        justify-content: unset;
                    }
                }
            }
        }
    }
</style>
